import type { FileLibraryDocument, FileLibraryGroup } from "@/api/file_library";
import { cn } from "@/lib/utils";
import { ChevronRight } from "lucide-react";
import { useMemo } from "react";

export type FileCenterBreadcrumbsProps = {
  rootLabel: string;
  groups: FileLibraryGroup[];
  browseFolderId: number | null;
  onBrowseFolder: (folderId: number | null) => void;
  /** When set, shown as the final segment (e.g. file preview). */
  currentDocument?: FileLibraryDocument | null;
};

const SEGMENT_BUTTON_CLASS =
  "max-w-[11rem] shrink-0 truncate rounded px-1.5 py-0.5 font-medium transition-colors hover:bg-muted";

function BreadcrumbChevron() {
  return <ChevronRight className="h-4 w-4 shrink-0 text-muted-foreground" aria-hidden />;
}

export function FileCenterBreadcrumbs({
  rootLabel,
  groups,
  browseFolderId,
  onBrowseFolder,
  currentDocument = null,
}: FileCenterBreadcrumbsProps) {
  const groupsById = useMemo(() => new Map(groups.map((g) => [g.id, g])), [groups]);

  const breadcrumb = useMemo(() => {
    const trail: FileLibraryGroup[] = [];
    let currentId = browseFolderId;
    while (currentId != null) {
      const group = groupsById.get(currentId);
      if (!group) break;
      trail.unshift(group);
      currentId = group.parent_id ?? null;
    }
    return trail;
  }, [browseFolderId, groupsById]);

  const atRoot = browseFolderId == null && currentDocument == null;
  const collapseMiddle = breadcrumb.length > 2;
  const hiddenFolders = collapseMiddle ? breadcrumb.slice(0, -2) : [];
  const visibleFolders = collapseMiddle ? breadcrumb.slice(-2) : breadcrumb;

  return (
    <div className="flex min-w-0 items-center gap-1 whitespace-nowrap pr-2">
      <button
        type="button"
        onClick={() => onBrowseFolder(null)}
        aria-current={atRoot ? "page" : undefined}
        className={cn(
          SEGMENT_BUTTON_CLASS,
          atRoot ? "text-foreground" : "text-muted-foreground",
        )}
        title={rootLabel}
      >
        {rootLabel}
      </button>
      {hiddenFolders.length > 0 ? (
        <span className="flex shrink-0 items-center gap-1">
          <BreadcrumbChevron />
          <button
            type="button"
            onClick={() => onBrowseFolder(hiddenFolders[0].id)}
            className="shrink-0 rounded px-1.5 py-0.5 text-muted-foreground hover:bg-muted"
            title={hiddenFolders.map((folder) => folder.name).join(" / ")}
            aria-label={`Folders: ${hiddenFolders.map((folder) => folder.name).join(", ")}`}
          >
            …
          </button>
        </span>
      ) : null}
      {visibleFolders.map((folder) => {
        const isCurrentFolder = !currentDocument && folder.id === browseFolderId;
        return (
          <span key={folder.id} className="flex shrink-0 items-center gap-1">
            <BreadcrumbChevron />
            <button
              type="button"
              onClick={() => onBrowseFolder(folder.id)}
              aria-current={isCurrentFolder ? "page" : undefined}
              className={cn(
                SEGMENT_BUTTON_CLASS,
                isCurrentFolder ? "text-foreground" : "text-muted-foreground",
              )}
              title={folder.name}
            >
              {folder.name}
            </button>
          </span>
        );
      })}
      {currentDocument && (
        <span className="flex min-w-0 items-center gap-1">
          <BreadcrumbChevron />
          <span
            className="min-w-0 max-w-[14rem] shrink truncate rounded px-1.5 py-0.5 font-medium text-foreground"
            title={currentDocument.name}
            aria-current="page"
          >
            {currentDocument.name}
          </span>
        </span>
      )}
    </div>
  );
}

export function findGroupIdForDocument(documentId: number, groups: FileLibraryGroup[]): number | null {
  for (const group of groups) {
    if (group.document_ids.includes(documentId)) return group.id;
  }
  return null;
}
